import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { addToCart } from '../../redux/cartSlice';
import { hasVariants, getPriceRange } from '../../utils/variants';
import { useCurrency, formatPrice } from '../../utils/currency';
import WhatsAppEnquiryButton from '../../common/whatsappEnquiryButton';
import { useGetProducts } from '../../backend/hooks';
import { Skeleton, ProductCardSkeleton } from '../../common/skeleton';

// Product Card showing the first product image – whole card clickable
const ProductCard = ({ product }) => {
  const images = product.images || [];
  const dispatch = useDispatch();
  const { currency, isPakistan } = useCurrency();

  const getImageUrl = (path) => {
    if (!path) return 'https://via.placeholder.com/400x400?text=No+Image';
    return process.env.REACT_APP_BE_URL + path;
  };

  const shortDescription = product.description?.length > 100
    ? product.description.substring(0, 100) + '...'
    : product.description;

  const handleAddToCart = (e) => {
    // Products with options have no single price, so let the click through to
    // the detail page where one can be chosen.
    if (hasVariants(product)) return;
    e.preventDefault(); // prevent any default behaviour
    e.stopPropagation(); // prevent navigation to product detail
    dispatch(addToCart(product));
  };

  return (
    <Link
      to={`/ProductDetail/${product.id}`}
      className="bg-surface-container-lowest rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all p-4 flex flex-col"
    >
      {/* Product image */}
      <div className="aspect-square rounded-2xl overflow-hidden bg-surface mb-6 relative group">
        <img
          src={getImageUrl(images[0])}
          alt={product.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
      </div>

      <div className="px-2 pb-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-outline mb-1 block">
          {product.category_name || 'General'}
        </span>
        <h4 className="font-headline font-bold text-lg text-on-surface mb-1 line-clamp-2 group-hover:text-primary transition-colors">
          {product.title}
        </h4>
        <p className="text-on-surface-variant text-sm mb-3 line-clamp-2">
          {shortDescription}
        </p>
        <p className="text-primary font-bold mb-4">
          {getPriceRange(product, currency).from && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant mr-1">
              From
            </span>
          )}
          {formatPrice(getPriceRange(product, currency))}
        </p>
        {isPakistan ? (
          <button
            type="button"
            onClick={handleAddToCart}
            className="w-full py-3 bg-primary text-on-primary rounded-xl font-semibold active:opacity-80 transition-opacity"
          >
            {hasVariants(product) ? 'Choose an Option' : 'Add to Cart'}
          </button>
        ) : (
          <WhatsAppEnquiryButton
            product={product}
            currency={currency}
            asButton
            className="w-full py-3 rounded-xl"
          />
        )}
      </div>
    </Link>
  );
};

export default function BestSeller() {
  const { data, isLoading, error } = useGetProducts();
  const products = data?.products || [];

  if (isLoading) {
    return (
      <section className="py-24 bg-surface-container-low">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <Skeleton className="h-10 w-96 max-w-full rounded-lg mb-3" />
            <Skeleton className="h-4 w-full max-w-2xl rounded mb-2" />
            <Skeleton className="h-4 w-2/3 max-w-lg rounded" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {Array.from({ length: 3 }).map((_, idx) => (
              <ProductCardSkeleton key={idx} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-24 bg-surface-container-low">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="bg-error/10 text-error p-4 rounded-xl">
            Failed to load products. Please try again later.
          </div>
        </div>
      </section>
    );
  }

  if (products.length === 0) {
    return (
      <section className="py-24 bg-surface-container-low">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-on-surface-variant">No products found.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 bg-surface-container-low">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex justify-between items-end mb-16">
          <div>
            <h2 className="font-headline text-4xl font-extrabold text-on-surface mb-2">
              Recently Added Products
            </h2>
            <p className="text-on-surface-variant">
              Discover the latest therapy equipment, rehabilitation solutions, and home care
              products added to the Kinetix collection.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      </div>
    </section>
  );
}